"use client"

import Image from "next/image"
import Link from "next/link"
import { ArrowRight, Mail } from "lucide-react"
import { motion, MotionConfig } from "motion/react"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { JsonLd } from "@/components/seo/json-ld"
import { BLOG_AUTHORS } from "@/data/authors"
import { Reveal } from "@/components/motion"
import { absoluteBrandAssetUrl, BRAND_ASSET_PATHS } from "@/lib/brand-assets"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"

export function AboutPageContent() {
  const aboutPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "About What Grass Is This?",
    description:
      "Learn about our mission to simplify lawn care with photo-based grass identification and personalized care recommendations.",
    url: "https://whatgrassisthis.com/about",
    mainEntity: {
      "@type": "Organization",
      name: "What Grass Is This?",
      description:
        "Lawn care platform that identifies grass types from photos and provides personalized care recommendations",
      url: "https://whatgrassisthis.com",
      logo: absoluteBrandAssetUrl(BRAND_ASSET_PATHS.iconDefault),
      foundingDate: "2025-07",
      knowsAbout: [
        "Lawn Care",
        "Grass Identification",
        "Lawn Maintenance",
        "Turf Management",
        "Gardening",
      ],
      areaServed: "United States",
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        email: "support@whatgrassisthis.com",
        availableLanguage: "English",
      },
    },
  }

  return (
    <MotionConfig reducedMotion="user">
      <div className="min-h-screen overflow-x-clip relative">
        {/* Ambient liquid-glass field — soft brand-green wash + blurred blobs the
            ds-glass founder/team/step cards refract against. Behind all content.
            Mirrors the /products ambient (components/ui/products-page-hero.tsx). */}
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute inset-0 bg-linear-to-b from-green-50/40 via-transparent to-emerald-50/30" />
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[680px] w-[1200px] rounded-full bg-green-200/40 blur-[130px]" />
          <div className="absolute top-[40%] -right-48 h-[520px] w-[520px] rounded-full bg-emerald-200/30 blur-[130px]" />
          <div className="absolute bottom-24 -left-48 h-[500px] w-[500px] rounded-full bg-amber-100/35 blur-[130px]" />
        </div>
        <JsonLd id="about-page-schema" data={aboutPageSchema} />

        <div
          className="mx-auto flex max-w-6xl flex-col gap-20 px-4 pt-6 pb-12 md:gap-[80px] md:px-6 md:pt-8 md:pb-14 lg:px-8"
        >
          <Hero />
          <WhyWeBuilt />
          <HowItWorks />
          <Founder />
          <EditorialTeam />
          <FinalCTA />
        </div>

      </div>
    </MotionConfig>
  )
}

function Hero() {
  return (
    <motion.section {...FADE_UP} transition={FADE_TRANSITION} className="flex flex-col gap-5">
      <BreadcrumbComponent items={[{ label: "Home", href: "/" }, { label: "About" }]} />
      <h1
        className="font-editorial m-0 max-w-[20ch] font-extrabold leading-[1.07] tracking-[-0.03em] text-[#14241b]"
        style={{ fontSize: "clamp(2.2rem, 3.2vw + 1rem, 3.5rem)" }}
      >
        Snap a photo. Know your <span className="text-green-600">grass</span>. Grow a better lawn.
      </h1>
      <p className="m-0 max-w-[60ch] text-base leading-relaxed text-gray-700 md:text-[17px]">
        What Grass Is This? identifies your grass type from a photo, then gives you a care plan
        tuned to your climate, soil, and season. No more guessing which fertilizer to buy or when
        to water.
      </p>
      <div className="mt-1 flex gap-3">
        <Link
          href="/"
          className="inline-flex min-h-[52px] items-center gap-2 rounded-xl px-6 text-base font-semibold text-white transition hover:-translate-y-px"
          style={{ background: "var(--grad-brand)", boxShadow: "var(--shadow-brand)" }}
        >
          Identify your grass
          <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
        </Link>
      </div>
    </motion.section>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs font-bold uppercase tracking-[0.06em] text-green-700">{children}</div>
  )
}

function WhyWeBuilt() {
  return (
    <Reveal onMount as="section" className="grid gap-10 md:grid-cols-[180px_1fr] md:gap-14">
      <SectionLabel>Why we built this</SectionLabel>
      <div className="flex max-w-[62ch] flex-col gap-[18px]">
        <p className="m-0 text-[18px] font-medium leading-[1.6] text-gray-900 md:text-[19px]">
          Most lawn care advice treats every lawn the same. But Kentucky Bluegrass in Minnesota has
          completely different needs than Bermuda in Texas. Generic tips lead to wasted money, dead
          patches, and frustration.
        </p>
        <p className="m-0 text-base leading-[1.7] text-gray-700">
          We built this to fix that. You upload a photo of your lawn, our model shows the most
          likely grass type with a confidence signal, and you get a care plan built for your grass,
          your climate, and your soil. It tells you what to do each month: when to fertilize, how
          high to mow, how much to water, and which products to use.
        </p>
        <p className="m-0 text-base leading-[1.7] text-gray-700">
          It's lawn care that actually works, because it's built around what's growing in your yard.
        </p>
      </div>
    </Reveal>
  )
}

function HowItWorks() {
  return (
    <Reveal onMount as="section" className="flex flex-col gap-7">
      <div className="flex max-w-[60ch] flex-col gap-2">
        <SectionLabel>How it works</SectionLabel>
        <h2 className="font-editorial m-0 text-[26px] font-extrabold leading-[1.15] tracking-[-0.025em] text-[#14241b] md:text-[30px]">
          From photo to plan in three steps.
        </h2>
      </div>
      <div className="grid gap-7 md:grid-cols-3">
        <Step
          n={1}
          title="Snap a photo"
          blurb="Get close and focus on a single patch. Upload a JPG, PNG, WebP, HEIC, or HEIF photo under 25 MB."
          illustration={<StepPhoto />}
        />
        <Step
          n={2}
          title="We identify the grass"
          blurb="Our model compares blade width, growth pattern, and texture against the grass types most common in US lawns."
          illustration={<StepIdentify />}
        />
        <Step
          n={3}
          title="You get a plan"
          blurb="A month-by-month schedule built around your grass type, your climate zone, and your soil. Editable any time."
          illustration={<StepPlan />}
        />
      </div>
    </Reveal>
  )
}

function Step({
  n,
  title,
  blurb,
  illustration,
}: {
  n: number
  title: string
  blurb: string
  illustration: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-[14px]">
      <div
        className="flex aspect-[5/3] w-full items-end justify-center overflow-hidden rounded-2xl border border-white/55 px-3 pt-5 backdrop-blur-md"
        style={{
          background: "linear-gradient(180deg, rgba(220,252,231,0.5), rgba(255,255,255,0.35))",
          boxShadow: "0 1px 0 rgba(255,255,255,0.6) inset, 0 12px 30px -16px rgba(15,40,25,0.2)",
        }}
      >
        {illustration}
      </div>
      <div className="flex items-center gap-[10px] text-xs font-bold uppercase tracking-[0.06em] text-green-700">
        <span className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full border-[1.5px] border-green-600 bg-white text-xs font-bold tracking-normal text-green-600">
          {n}
        </span>
        Step {n}
      </div>
      <h3 className="m-0 text-xl font-semibold tracking-[-0.01em] text-gray-900">{title}</h3>
      <p className="m-0 text-[15px] leading-[1.6] text-gray-700">{blurb}</p>
    </div>
  )
}

function StepPhoto() {
  return (
    <svg
      viewBox="0 0 200 120"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMax meet"
      aria-hidden="true"
    >
      <g fill="none" stroke="#16a34a" strokeLinecap="round" opacity="0.55">
        <path d="M30 110 L 28 30" strokeWidth="3.2" />
        <path d="M50 110 L 52 22" strokeWidth="3.2" />
        <path d="M170 110 L 172 28" strokeWidth="3.2" />
        <path d="M186 110 L 184 36" strokeWidth="2.8" />
      </g>
      <rect x="78" y="22" width="50" height="78" rx="8" fill="#fff" stroke="#0f1d14" strokeWidth="1.5" />
      <rect x="84" y="32" width="38" height="48" rx="3" fill="#f0fdf4" stroke="#16a34a" strokeWidth="1" />
      <g fill="none" stroke="#16a34a" strokeLinecap="round" strokeWidth="1.4">
        <path d="M90 78 L 89 50" />
        <path d="M98 78 L 99 44" />
        <path d="M106 78 L 105 48" />
        <path d="M114 78 L 116 46" />
      </g>
      <circle cx="103" cy="88" r="3" fill="none" stroke="#0f1d14" strokeWidth="1.2" />
      <line x1="20" y1="111" x2="180" y2="111" stroke="#0f1d14" strokeWidth="1" opacity="0.2" strokeLinecap="round" />
    </svg>
  )
}

function StepIdentify() {
  return (
    <svg
      viewBox="0 0 200 120"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMax meet"
      aria-hidden="true"
    >
      <g fill="none" stroke="#16a34a" strokeLinecap="round">
        <path d="M70 110 L 68 34" strokeWidth="3.2" />
        <path d="M100 110 L 100 18" strokeWidth="3.6" />
        <path d="M130 110 L 134 30" strokeWidth="3.2" />
      </g>
      <circle cx="100" cy="50" r="22" fill="rgba(255,255,255,0.85)" stroke="#0f1d14" strokeWidth="1.8" />
      <line x1="116" y1="66" x2="132" y2="82" stroke="#0f1d14" strokeWidth="3" strokeLinecap="round" />
      <rect x="124" y="20" width="56" height="14" rx="3" fill="#fff" stroke="#16a34a" strokeWidth="1.2" />
      <text
        x="152"
        y="30"
        textAnchor="middle"
        fontSize="8"
        fontFamily="Inter, sans-serif"
        fontWeight="600"
        fill="#16a34a"
      >
        Tall Fescue
      </text>
      <line
        x1="124"
        y1="27"
        x2="106"
        y2="40"
        stroke="#16a34a"
        strokeWidth="1"
        strokeDasharray="2 2"
      />
      <line x1="20" y1="111" x2="180" y2="111" stroke="#0f1d14" strokeWidth="1" opacity="0.2" strokeLinecap="round" />
    </svg>
  )
}

function StepPlan() {
  const rows = [44, 58, 72, 86]
  const lineEnds = [130, 122, 134, 118]
  return (
    <svg
      viewBox="0 0 200 120"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMax meet"
      aria-hidden="true"
    >
      <g fill="none" stroke="#16a34a" strokeLinecap="round" opacity="0.45">
        <path d="M28 110 L 28 50" strokeWidth="2.4" />
        <path d="M170 110 L 172 56" strokeWidth="2.4" />
        <path d="M186 110 L 184 62" strokeWidth="2.2" />
      </g>
      <rect x="56" y="20" width="92" height="76" rx="6" fill="#fff" stroke="#0f1d14" strokeWidth="1.5" />
      <line x1="56" y1="34" x2="148" y2="34" stroke="#0f1d14" strokeWidth="1.5" />
      <circle cx="64" cy="27" r="1.6" fill="#0f1d14" />
      <circle cx="72" cy="27" r="1.6" fill="#0f1d14" />
      <circle cx="140" cy="27" r="1.6" fill="#0f1d14" />
      {rows.map((y, i) => (
        <g key={i}>
          <rect
            x="64"
            y={y - 4}
            width="8"
            height="8"
            rx="1.5"
            fill={i < 2 ? "#16a34a" : "none"}
            stroke="#16a34a"
            strokeWidth="1.2"
          />
          {i < 2 && (
            <path
              d={`M65.5 ${y} L 67.5 ${y + 1.5} L 70.5 ${y - 2}`}
              stroke="#fff"
              strokeWidth="1.4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          <line
            x1="78"
            y1={y}
            x2={lineEnds[i]}
            y2={y}
            stroke="#374151"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        </g>
      ))}
      <line x1="20" y1="111" x2="180" y2="111" stroke="#0f1d14" strokeWidth="1" opacity="0.2" strokeLinecap="round" />
    </svg>
  )
}

function FounderTuft() {
  return (
    <svg
      viewBox="0 0 200 140"
      width="100%"
      height="100%"
      preserveAspectRatio="xMidYMax meet"
      aria-hidden="true"
    >
      <g fill="none" stroke="#16a34a" strokeLinecap="round">
        <path d="M40 130 L 38 70" strokeWidth="3" opacity="0.5" />
        <path d="M58 130 L 60 50" strokeWidth="3.4" opacity="0.7" />
        <path d="M78 130 L 76 32" strokeWidth="3.6" />
        <path d="M100 130 L 100 18" strokeWidth="3.8" />
        <path d="M122 130 L 124 30" strokeWidth="3.6" />
        <path d="M142 130 L 140 48" strokeWidth="3.4" opacity="0.7" />
        <path d="M160 130 L 162 68" strokeWidth="3" opacity="0.5" />
      </g>
      <line
        x1="20"
        y1="131"
        x2="180"
        y2="131"
        stroke="#0f1d14"
        strokeWidth="1"
        opacity="0.2"
        strokeLinecap="round"
      />
    </svg>
  )
}

function Founder() {
  return (
    <Reveal onMount as="section" className="ds-glass grid items-start gap-8 rounded-3xl p-8 md:grid-cols-[200px_1fr] md:gap-12 md:p-11">
      <div
        className="flex h-[200px] w-full items-end justify-center overflow-hidden rounded-2xl border border-white/50 px-3 pt-5 backdrop-blur-md md:w-[200px]"
        style={{ background: "linear-gradient(180deg, rgba(220,252,231,0.55), rgba(255,255,255,0.3))" }}
      >
        <FounderTuft />
      </div>
      <div className="flex flex-col gap-[14px] pt-1.5">
        <SectionLabel>Founder</SectionLabel>
        <h2 className="font-editorial m-0 text-[26px] font-extrabold tracking-[-0.025em] text-[#14241b] md:text-[30px]">
          Hi, I'm Anthony.
        </h2>
        <p className="m-0 max-w-[58ch] text-base leading-[1.7] text-gray-700">
          I built What Grass Is This? after years of throwing money at the wrong fertilizer and
          following advice that didn't apply to my yard. I'm a software engineer by trade, and a
          lawn obsessive on weekends. This site is a one-person project, run honestly. If something
          on it is wrong, I want to know.
        </p>
        <div className="mt-1.5 flex flex-wrap gap-[18px] text-sm">
          <a
            href="mailto:support@whatgrassisthis.com"
            className="inline-flex items-center gap-1.5 font-medium text-green-600 hover:text-green-700"
          >
            <Mail className="h-3.5 w-3.5" />
            Email me
          </a>
          <a
            href="https://www.linkedin.com/in/anthonytreviso"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-medium text-green-600 hover:text-green-700"
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.37V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zm1.78 13.02H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z" />
            </svg>
            LinkedIn
          </a>
        </div>
      </div>
    </Reveal>
  )
}

function EditorialTeam() {
  return (
    <Reveal onMount as="section" className="flex flex-col gap-7">
      <div className="flex max-w-[60ch] flex-col gap-2">
        <SectionLabel>Editorial team</SectionLabel>
        <h2 className="font-editorial m-0 text-[26px] font-extrabold leading-[1.15] tracking-[-0.025em] text-[#14241b] md:text-[30px]">
          Articles written by working lawn care specialists.
        </h2>
        <p className="m-0 max-w-[62ch] text-base leading-[1.7] text-gray-700">
          Guides and blog posts are written by our team of three specialists, each
          covering their area of hands-on experience. Anthony reviews and publishes
          everything before it goes live.
        </p>
      </div>
      <div className="grid gap-5 md:grid-cols-3">
        {BLOG_AUTHORS.map((author) => (
          <Link
            key={author.slug}
            href={`/blog/author/${author.slug}`}
            className="ds-glass flex flex-col gap-3 rounded-[18px] p-5 transition duration-300 ease-out hover:-translate-y-1"
          >
            <div className="flex items-center gap-3">
              <Image
                src={author.headshot.url}
                alt={author.headshot.alt}
                width={48}
                height={48}
                className="h-12 w-12 rounded-full object-cover"
              />
              <div className="flex flex-col">
                <span className="text-[15px] font-semibold text-gray-900">
                  {author.name}
                </span>
                <span className="text-[13px] text-gray-600">
                  {author.shortCredential}
                </span>
              </div>
            </div>
            <p className="m-0 text-[15px] leading-[1.6] text-gray-700">
              {author.bio.short}
            </p>
            <span className="mt-auto inline-flex items-center gap-1 text-[13px] font-medium text-green-700">
              Read full bio
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
            </span>
          </Link>
        ))}
      </div>
    </Reveal>
  )
}

function FinalCTA() {
  return (
    <Reveal onMount as="section" className="flex flex-col items-start gap-4 pt-6">
      <h2 className="font-editorial m-0 max-w-[24ch] text-[28px] font-extrabold leading-[1.15] tracking-[-0.025em] text-[#14241b] md:text-[32px]">
        Ready to find out what's growing in your yard?
      </h2>
      <div className="mt-1.5 flex flex-wrap items-center gap-[18px]">
        <Link
          href="/"
          className="inline-flex min-h-[52px] items-center gap-2 rounded-xl px-6 text-base font-semibold text-white transition hover:-translate-y-px"
          style={{ background: "var(--grad-brand)", boxShadow: "var(--shadow-brand)" }}
        >
          Identify your grass
          <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
        </Link>
        <Link
          href="/contact"
          className="text-sm font-medium text-gray-700 underline decoration-gray-200 underline-offset-4 hover:text-gray-900"
        >
          Or contact us
        </Link>
      </div>
    </Reveal>
  )
}
